/**
 * Patron-facing routes. Owner: public site.
 *
 * Each export returns a Fragment of <Route> elements for <Routes> (App.js places
 * them under a layout). They are functions, not constants, so flag() is read on
 * every render — runtime flags from GET /api/flags can switch a route on.
 */
import React, { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { flag } from "../config";
import { RequireAuth } from "../components/routing/guards";
import Home from "../pages/Home";
import Businesses from "../pages/Businesses";
import BusinessLanding from "../pages/BusinessLanding";
import ForVenues from "../pages/ForVenues";
import ForBusinesses from "../pages/ForBusinesses";
import NotFound from "../pages/NotFound";
import Pricing from "../pages/Pricing";
import JoinTappd from "../pages/JoinTappd";
import AlphaThankYou from "../pages/AlphaThankYou";
import Profile from "../pages/ProfileNew";
import VerifyEmail from "../pages/VerifyEmail";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import Achievements from "../pages/Achievements";
import Scan from "../pages/ScanNew";
import StopTheTrace from "../pages/minigame/StopTheTrace";
import PickRightLine from "../pages/minigame/PickRightLine";
import TapLockCode from "../pages/minigame/TapLockCode";
import ClassifiedFolder from "../pages/minigame/ClassifiedFolder";
import AuthorizationQuiz from "../pages/minigame/AuthorizationQuiz";
import Redeem from "../pages/Redeem";
import Privacy from "../pages/Privacy";
import Terms from "../pages/Terms";
import PitchDeck from "../pages/PitchDeck";
import EmailPreferences from "../pages/EmailPreferences";
import MyCodes from "../pages/MyCodes";
import HowItWorks from "../pages/HowItWorks";
import SignupPage from "../pages/SignupPage";
import FounderStory from "../pages/FounderStory";
import Rewards from "../pages/Rewards";
import Leaderboard from "../pages/Leaderboard";
import InstantRedeem from "../pages/InstantRedeem";
import AcceptBusinessInvite from "../pages/AcceptBusinessInvite";
import CoasterGallery from "../pages/CoasterGallery";
import { PageLoadError } from "../layouts/PageStates";
// Slot-model public pages (Phase 5c, SLOT_MODEL): same URLs as the pages above, swapped
// per render by flag('SLOT_MODEL'); /venues and /free-listing exist only with the flag on.
import HomeSlot from "../pages/public/HomeSlot";
import HowItWorksSlot from "../pages/public/HowItWorksSlot";
import TermsSlot from "../pages/public/TermsSlot";
import PrivacySlot from "../pages/public/PrivacySlot";
import ForBusinessesSlot from "../pages/public/ForBusinessesSlot";
import ForVenuesSlot from "../pages/public/ForVenuesSlot";
import PricingSlot from "../pages/public/PricingSlot";

/**
 * A route page loaded on demand (its own chunk). PublicLayout's Suspense shows
 * PageLoading meanwhile; if the chunk cannot be fetched the route shows PageLoadError
 * (with a reload button) instead of breaking the app. `displayName` keeps the page's
 * name in route tooling and tests.
 */
export const lazyPage = (name, load) => {
  const Page = lazy(() => load().catch(() => ({ default: PageLoadError })));
  Page.displayName = name;
  return Page;
};
// The two flag-only pages are the heaviest (the sign-up form brings react-hook-form +
// zod), so they load on demand and stay out of the main chunk (plan: main JS <= +10%).
const VenueAvailability = lazyPage("VenueAvailability", () => import("../pages/public/VenueAvailability"));
const FreeListingSignup = lazyPage("FreeListingSignup", () => import("../pages/public/FreeListingSignup"));
// Phase 6c (U1): the venue deals page (/local-deals-near-me) and the scan landing (/scan)
// swapped in by SLOT_MODEL; ScanLanding renders VenueDeals' content inline.
const VenueDeals = lazyPage("VenueDeals", () => import("../pages/public/VenueDeals"));
const ScanLanding = lazyPage("ScanLanding", () => import("../pages/public/ScanLanding"));
// Phase 6c (U3): the deal wallet for guests (signed email link) and account holders.
const Wallet = lazyPage("Wallet", () => import("../pages/public/Wallet"));
// Phase 6c (U2): /business/:slug (+ /business/:slug/:page) by slot tier with SLOT_MODEL.
const BusinessPage = lazyPage("BusinessPage", () => import("../pages/public/BusinessPage"));
// Site redesign Phase 3: /our-story is the redesigned founder letter with SLOT_MODEL (the old
// FounderStory stays for flag-off); a new page, so it loads on demand like the pages above.
const OurStorySlot = lazyPage("OurStorySlot", () => import("../pages/public/OurStorySlot"));
// Site redesign Phase 5: /venue/apply is the redesigned public venue application with
// SLOT_MODEL (three steps, no sign-up first, POST /api/venue/applications), mounted here so
// it gets the site chrome. Flag off, routes/venue.jsx keeps the old VenueApplication (bare).
const VenueApplySlot = lazyPage("VenueApplySlot", () => import("../pages/public/VenueApplySlot"));

/**
 * Public pages with the full site chrome (PublicLayout).
 *
 * SLOT_MODEL (Phase 5c) swaps the component on the same URL for /, /for-businesses,
 * /for-venues, /how-it-works, /pricing, /privacy and /terms (the old pages stay for
 * flag-off; the site redesign adds /our-story), and adds /venues and /free-listing only
 * while the flag is on (flag-off they fall through to the PublicLayout 404, as before). Phase 6c: /local-deals-near-me
 * swaps to VenueDeals, and /scan (ScanLanding) moves here from patronFullscreenRoutes
 * while the flag is on. Site redesign Phase 5: /venue/apply (the public venue application)
 * lives here with the flag on; flag off it stays the bare VenueApplication in routes/venue.jsx.
 */
export function publicRoutes() {
  const slot = flag('SLOT_MODEL');
  return (
    <>
      <Route path="/" element={slot ? <HomeSlot /> : <Home />} />
      <Route path="/local-deals-near-me" element={slot ? <VenueDeals /> : <Businesses />} />
      {/* Phase 6c: with SLOT_MODEL the scan page is the public front door, so it gets the
          site chrome here; flag off it stays ScanNew in patronFullscreenRoutes (PortalLayout). */}
      {slot && <Route path="/scan" element={<ScanLanding />} />}
      {/* Redirect old /businesses route to new /local-deals-near-me */}
      <Route path="/businesses" element={<Navigate to="/local-deals-near-me" replace />} />
      {/* Phase 6c (U2): the business page by slot tier (Premium landing, Sponsor microsite,
          Standard profile) with SLOT_MODEL; microsite pages live at /business/:slug/:page.
          Portal paths (/business/deals/new, /business/deals/*, ...) still win: static
          segments outrank :params in React Router's ranking. */}
      <Route path="/business/:slug" element={slot ? <BusinessPage /> : <BusinessLanding />} />
      {slot && <Route path="/business/:slug/:page" element={<BusinessPage />} />}
      <Route path="/for-businesses" element={slot ? <ForBusinessesSlot /> : <ForBusinesses />} />
      <Route path="/for-venues" element={slot ? <ForVenuesSlot /> : <ForVenues />} />
      {slot && <Route path="/venues" element={<VenueAvailability />} />}
      {slot && <Route path="/free-listing" element={<FreeListingSignup />} />}
      {slot && <Route path="/venue/apply" element={<VenueApplySlot />} />}
      <Route path="/how-it-works" element={slot ? <HowItWorksSlot /> : <HowItWorks />} />
      <Route path="/our-story" element={slot ? <OurStorySlot /> : <FounderStory />} />
      {flag('EXTRA_FORMS') && <Route path="/join-tappd" element={<JoinTappd />} />}
      <Route path="/pricing" element={slot ? <PricingSlot /> : <Pricing />} />
      {flag('EXTRA_FORMS') && <Route path="/alpha-signup" element={<JoinTappd />} />}
      {flag('EXTRA_FORMS') && <Route path="/alpha-thank-you" element={<AlphaThankYou />} />}
      <Route path="/signup" element={<SignupPage />} />
      {/* Redirect old /dashboard and /my-codes to new /deal-wallet */}
      <Route path="/dashboard" element={<Navigate to="/deal-wallet" replace />} />
      <Route path="/my-codes" element={<Navigate to="/deal-wallet" replace />} />
      {/* Phase 6c (U3): with SLOT_MODEL /deal-wallet opens without a login (a guest's signed
          link, a signed-in account, or the "email me a new link" form); flag off it stays
          MyCodes behind RequireAuth. */}
      {slot && <Route path="/deal-wallet" element={<Wallet />} />}
      <Route element={<RequireAuth />}>
        <Route path="/profile" element={<Profile />} />
        {!slot && <Route path="/deal-wallet" element={<MyCodes />} />}
      </Route>
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-business-invite" element={<AcceptBusinessInvite />} />
      {flag('REWARDS') && <Route path="/achievements" element={<Achievements />} />}
      {flag('REWARDS') && <Route path="/rewards" element={<Rewards />} />}
      {flag('REWARDS') && <Route path="/leaderboard/:hubId" element={<Leaderboard />} />}

      {/* Public Shareable Pages */}
      {flag('COASTER_GALLERY') && <Route path="/coasters/gallery" element={<CoasterGallery />} />}
      <Route path="/privacy" element={slot ? <PrivacySlot /> : <Privacy />} />
      <Route path="/terms" element={slot ? <TermsSlot /> : <Terms />} />
    </>
  );
}

/**
 * Full-screen patron flows without site chrome (PortalLayout): scan (SLOT_MODEL off
 * only), redeem, minigames, the hidden pitch deck. A flagged-off path here still renders
 * NotFound without chrome, as it did when chrome was decided by path.
 */
export function patronFullscreenRoutes() {
  return (
    <>
      {/* SLOT_MODEL on: /scan is ScanLanding under PublicLayout (publicRoutes above). */}
      {!flag('SLOT_MODEL') && <Route path="/scan" element={<Scan />} />}
      <Route path="/redeem" element={<Redeem />} />
      {flag('INSTANT_REDEEM') && <Route path="/instant-redeem" element={<InstantRedeem />} />}
      {!flag('INSTANT_REDEEM') && <Route path="/instant-redeem" element={<NotFound />} />}

      {/* Mini Game Routes */}
      {flag('REWARDS') && <Route path="/minigame/stop-the-trace" element={<StopTheTrace />} />}
      {flag('REWARDS') && <Route path="/minigame/pick-right-line" element={<PickRightLine />} />}
      {flag('REWARDS') && <Route path="/minigame/tap-lock-code" element={<TapLockCode />} />}
      {flag('REWARDS') && <Route path="/minigame/classified-folder" element={<ClassifiedFolder />} />}
      {flag('REWARDS') && <Route path="/minigame/authorization-quiz" element={<AuthorizationQuiz />} />}
      <Route path="/minigame/*" element={<NotFound />} />

      {/* Hidden Pitch Deck Route - Not linked in navigation */}
      {flag('PITCH_DECK') && <Route path="/pitch" element={<PitchDeck />} />}
      {!flag('PITCH_DECK') && <Route path="/pitch" element={<NotFound />} />}
    </>
  );
}

/**
 * Pages that paint their own branded header (no site chrome), but keep the
 * patron bottom nav — App.js mounts these under <PortalLayout bottomNav />.
 */
export function ownHeaderRoutes() {
  return (
    <>
      {/* Email Preferences Route - No auth required */}
      <Route path="/email-preferences" element={<EmailPreferences />} />
    </>
  );
}
